import React from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  PhotoIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const stats = [
  { name: 'Total Projects', value: '12', icon: BuildingOfficeIcon },
  { name: 'Photos Uploaded', value: '248', icon: PhotoIcon },
  { name: 'Client Inquiries', value: '24', icon: UserGroupIcon },
  { name: 'Messages', value: '18', icon: ChatBubbleLeftRightIcon },
];

const recentActivity = [
  {
    id: 1,
    type: 'project',
    title: 'Modern Mountain Estate',
    description: 'Project status updated to "In Progress"',
    date: '2 hours ago',
  },
  {
    id: 2,
    type: 'photo',
    title: 'Lakeside Villa',
    description: 'Added 12 new photos to the gallery',
    date: '5 hours ago',
  },
  {
    id: 3,
    type: 'inquiry',
    title: 'New Client Inquiry',
    description: 'John Smith requested information about custom builds',
    date: '1 day ago',
  },
];

const Dashboard = () => {
  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">
            Total Projects
          </h3>
          <p className="text-3xl font-bold text-accent-600">12</p>
          <p className="text-sm text-primary-500 mt-1">3 in progress</p>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">
            Active Users
          </h3>
          <p className="text-3xl font-bold text-accent-600">5</p>
          <p className="text-sm text-primary-500 mt-1">2 admins, 3 editors</p>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">
            Contact Requests
          </h3>
          <p className="text-3xl font-bold text-accent-600">8</p>
          <p className="text-sm text-primary-500 mt-1">3 new this week</p>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-primary-900">
            Recent Projects
          </h2>
          <Link
            to="/admin/projects"
            className="text-accent-600 hover:text-accent-700 font-medium"
          >
            View All →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-primary-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-primary-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-900">
                  Modern Mountain Estate
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    In Progress
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                  Aspen, CO
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                  2 days ago
                </td>
              </tr>
              {/* Add more project rows here */}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-primary-900 mb-4">
          Recent Activity
        </h2>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-accent-100 flex items-center justify-center">
                <span className="text-accent-600">✓</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-primary-900">
                Project "Modern Mountain Estate" updated
              </p>
              <p className="text-xs text-primary-500">2 hours ago</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-accent-100 flex items-center justify-center">
                <span className="text-accent-600">+</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-primary-900">
                New user "John Smith" added
              </p>
              <p className="text-xs text-primary-500">5 hours ago</p>
            </div>
          </div>
          {/* Add more activity items here */}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((item) => (
          <div
            key={item.name}
            className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6"
          >
            <dt>
              <div className="absolute rounded-md bg-accent-500 p-3">
                <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-primary-500">{item.name}</p>
            </dt>
            <dd className="ml-16 flex items-baseline">
              <p className="text-2xl font-semibold text-primary-900">{item.value}</p>
            </dd>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-primary-900">Quick Actions</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
          >
            Add New Project
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
          >
            Upload Photos
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
          >
            View Messages
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 